import { ArrowLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../Logo';
import { usePrefetchSubCategory } from '../../../pages/productCategories/subCategoryPage/useSubCategoryPage';

const departments = [
  {
    title: 'Electronics',
    items: [
      {
        name: 'Phones',
        icon: '/images/phone.min.svg',
        link: '/electronics/phones',
      },
      {
        name: 'Computers',
        icon: '/images/computers.min.svg',
        link: '/electronics/computers',
      },
      { name: 'TV', icon: '/images/tv.min.svg', link: '/electronics/tv' },
      {
        name: 'Home Appliances',
        icon: '/images/home-appliances.min.svg',
        link: '/electronics/home-appliances',
      },
      {
        name: 'Home Audio & Theater',
        icon: '/images/audio.min.svg',
        link: '/electronics/home-audio-theater',
      },
      {
        name: 'Gadgets',
        icon: '/images/gadgets.min.svg',
        link: '/electronics/gadgets',
      },
      {
        name: 'Video games',
        icon: '/images/games.min.svg',
        link: '/electronics/video-games',
      },
      {
        name: 'Accessories',
        icon: '/images/accessories.min.svg',
        link: '/electronics/accessories',
      },
    ],
  },
  {
    title: 'Housing',
    items: [
      {
        name: 'RentSmallSmall',
        icon: '/images/mobile-nav-rss-icon/rent-ss.png',
        link: '/real-estate/rent-smallsmall',
        external: false,
      },
      {
        name: 'StaySmallSmall',
        icon: '/images/mobile-nav-rss-icon/stayss.min.svg',
        link: '/real-estate/stay-smallsmall',
      },
      {
        name: 'BuySmallSmall',
        icon: '/images/mobile-nav-rss-icon/buyss.min.svg',
        link: '/real-estate/buy-smallsmall',
      },
    ],
  },
  {
    title: 'Lifestyle & Consumer goods',
    items: [
      {
        name: 'Personal care',
        icon: '/images/personal.min.svg',
        link: '/lifestyle-consumer-goods/personal-care',
      },
      {
        name: 'Beauty',
        icon: '/images/beauty.min.svg',
        link: '/lifestyle-consumer-goods/beauty',
      },
      {
        name: 'Fashion',
        icon: '/images/fashion.min.svg',
        link: '/lifestyle-consumer-goods/fashion',
      },
      {
        name: 'Health & wellness',
        icon: '/images/health.min.svg',
        link: '/lifestyle-consumer-goods/health-wellness',
      },
      {
        name: 'Baby & mother care',
        icon: '/images/baby.min.svg',
        link: '/lifestyle-consumer-goods/baby-mother-care',
      },
      {
        name: 'Household essentials',
        icon: '/images/household.min.svg',
        link: '/lifestyle-consumer-goods/household-essentials',
      },
      {
        name: 'Toys',
        icon: '/images/toys.min.svg',
        link: '/lifestyle-consumer-goods/toys',
      },
      {
        name: 'Entertainment',
        icon: '/images/entertainment.min.svg',
        link: '/lifestyle-consumer-goods/entertainment',
      },
      {
        name: 'Book',
        icon: '/images/books.min.svg',
        link: '/lifestyle-consumer-goods/books',
      },
    ],
  },
  {
    title: 'Home & Living',
    items: [
      { name: 'Decor', icon: '/images/decor.min.svg', link: '/home-living/decor' },
      {
        name: 'Furniture',
        icon: '/images/furniture.min.svg',
        link: '/home-living/furniture',
      },
      {
        name: 'Kitchen & Dining',
        icon: '/images/kitchen.min.svg',
        link: '/home-living/kitchen-dining',
      },
      {
        name: 'Home Improvement',
        icon: '/images/home.min.svg',
        link: '/home-living/home-improvement',
      },
      {
        name: 'Bed & Bath',
        icon: '/images/bed.min.svg',
        link: '/home-living/bed-bath',
      },
      {
        name: 'Garden & Outdoor',
        icon: '/images/garden.min.svg',
        link: '/home-living/garden-outdoor',
      },
    ],
  },
  {
    title: 'Food & Drink',
    items: [
      {
        name: 'Grocery',
        icon: '/images/grocery.min.svg',
        link: '/food-drink/grocery',
      },
      {
        name: 'Confectionery',
        icon: '/images/confectionery.min.svg',
        link: '/food-drink/confectionery',
      },
      {
        name: 'Beverages',
        icon: '/images/beverages.min.svg',
        link: '/food-drink/beverages',
      },
    ],
  },
  {
    title: 'Sales & promo',
    items: [
      {
        name: 'Bundle deals',
        icon: '/images/bundle-deals.min.svg',
        link: '/deals',
      },
      {
        name: 'Seasonal sales',
        icon: '/images/seasonal-sales.min.svg',
        link: '/deals',
      },
      { name: 'Promotions', icon: '/images/promotions.min.svg', link: '/deals' },
    ],
  },
];

const SubcategoryItem = ({ item, onCloseAll }) => {
  const prefetchProps = usePrefetchSubCategory();
  const content = (
    <div className="flex flex-col">
      <div className="w-full aspect-square bg-[#F5F5F7] rounded-xl flex items-center justify-center p-4 mb-2">
        <img
          src={item.icon}
          alt={item.name}
          className="w-32 h-32 object-contain"
        />
      </div>
      <span className=" text-gray-800">{item.name}</span>
    </div>
  );

  if (item.external) {
    return (
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onCloseAll}
      >
        {content}
      </a>
    );
  }
  return (
    <Link to={item.link} onClick={onCloseAll} {...prefetchProps(item.link)}>
      {content}
    </Link>
  );
};

const Shop = ({ isOpen, onClose, onCloseAll }) => {
  const [selectedDept, setSelectedDept] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      const t = setTimeout(() => {
        setVisible(false);
        setSelectedDept(null);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!visible && !isOpen) return null;

  const handleClose = () => {
    setSelectedDept(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />

      <div
        className={`absolute top-0 left-0 w-full h-full bg-white overflow-hidden transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          className="flex h-full transition-transform duration-300"
          style={{
            width: '200%',
            transform: selectedDept ? 'translateX(-50%)' : 'translateX(0)',
          }}
        >
          <div className="w-1/2 h-full flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <Logo />
              <button onClick={onCloseAll} className="p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 pb-0">
              <p className="text-lg font-semibold pb-2">Shop by departments</p>
              <div className="border-b-2 border-[#ECEDF1]" />
            </div>

            <ul className="flex-1 overflow-y-auto px-5 pt-2">
              {departments.map((dept) => (
                <li key={dept.title}>
                  <button
                    className="flex items-center justify-between w-full py-5 text-left"
                    onClick={() => setSelectedDept(dept)}
                  >
                    <span className="font-bold text-gray-900 text-xl">
                      {dept.title}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-900" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="w-1/2 h-full flex flex-col overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_40px] items-center px-5 pt-5 pb-4">
              <button onClick={() => setSelectedDept(null)} className="p-1">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="font-semibold text-gray-900 text-xl text-center">
                {selectedDept?.title}
              </span>
              <button onClick={onCloseAll} className="p-1 justify-self-end">
                <X className="w-5 h-5" />
              </button>
            </div>
            <hr className="mx-5" />

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-2 gap-4">
                {selectedDept?.items.map((item) => (
                  <SubcategoryItem
                    key={item.name}
                    item={item}
                    onCloseAll={onCloseAll}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
